import React, { useMemo, useState } from 'react';
import Table from '../../../../ui/Table/Table';
import {
    AdminBooking,
    AdminBookingTotals,
    XeroApexInvoiceSimple,
    AdminInfluencer,
    PatchAdminBookingData,
} from '@round/api';
import { Column } from 'react-table';
import styles from '../../../booking/TableCells/BookingsTable.module.css';
import cn from 'classnames';
import { asMoney, formatDate, formatYesNo } from '../../../../utils/utits';
import StatusPicker from '../../../booking/TableCells/StatusPicker/StatusPicker';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import NumberInput from '../../../../ui/DataEntry/NumberInput/NumberInput';
import CampaignTypeCell from '../../../booking/TableCells/CampaignTypeCell';
import InfoCell from '../../../booking/TableCells/InfoCell';
import BookingPriceCell from '../../../booking/TableCells/BookingPriceCell';
import PostLinkCell from '../../../booking/TableCells/PostLinkCell';
import BoostCodeCell from '../../../booking/TableCells/BoostCodeCell';
import FinancialReferenceCell from '../../../booking/TableCells/FinancialReferenceCell';
import ApproveToBillCell from '../../../booking/TableCells/ApproveToBillCell';
import { renderTotalsAsMoney } from '../../../booking/TableCells/helpers';
import InvoiceCell from '../../../booking/TableCells/InvoiceCell';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import range from 'lodash/range';
import CreatorCell from '../../../booking/TableCells/CreatorCell';
import ReasonsToBlockApprovalCell from '../../../booking/TableCells/ReasonsToBlockApprovalCell';
import PaymentTermsPicker from '../../../booking/TableCells/PaymentTermsPicker/PaymentTermsPicker';
import PostTypeCell from '../../../booking/TableCells/PostTypeCell';
import IGStoryUploadCell from '../../../booking/TableCells/IGStoryUpload/IGStoryUploadCell';

export type AdminBookingsTableRow = AdminBooking & {
    xero_invoice?: XeroApexInvoiceSimple;
    influencer?: AdminInfluencer;
};

type AdminTableColumn = Column<AdminBookingsTableRow> & { editable?: boolean; dropdown?: boolean };

type AdminBookingsTableProps = {
    className?: string;
    data: AdminBookingsTableRow[];
    hasErrorLoadingInvoices: boolean;
    totals: AdminBookingTotals | null;
    loading?: boolean;
    updateBooking: (bookingId: number, data: PatchAdminBookingData) => Promise<void>;
    onStatusChange?: (bookingId: number, status: AdminBooking['status']) => Promise<void>;
    approveToBill?: (booking: AdminBookingsTableRow) => void;
};

const AdminBookingsTable = ({
    className,
    data,
    loading,
    hasErrorLoadingInvoices,
    updateBooking,
    onStatusChange,
    approveToBill,
    totals,
}: AdminBookingsTableProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const columns = useMemo(() => {
        const columns: AdminTableColumn[] = [
            {
                Header: 'ID',
                accessor: 'id',
            },
            {
                Header: 'Creator',
                accessor: 'influencer_name',
                Cell: CreatorCell,
            },
            {
                Header: 'Post Type',
                accessor: 'post_type',
                Cell: PostTypeCell,
            },
            {
                Header: 'Type',
                accessor: 'campaign_type',
                Cell: CampaignTypeCell,
            },
            {
                Header: 'Manager',
                accessor: 'manager_email',
                Cell: InfoCell,
            },
            {
                Header: 'Client',
                accessor: 'client_name',
                Cell: InfoCell,
            },
            {
                Header: 'Booker',
                accessor: 'booker_email',
                Cell: InfoCell,
            },
            {
                Header: 'Campaign Name',
                accessor: 'campaign_name',
                Cell: (props) => <InfoCell {...props} className={styles.campaignName} />,
            },
            {
                Header: 'Date Created',
                accessor: 'created',
                Cell: ({ value }) => <>{formatDate(value, { month: 'numeric' })}</>,
            },
            {
                Header: 'Currency',
                accessor: 'currency',
                Cell: ({ value }) => <>{currencies.find((currency) => currency.id === value)?.name}</>,
            },
            {
                Header: 'Creator Asking Price',
                accessor: 'influencer_cost',
                Cell: ({
                    value,
                    row: {
                        original: { currency },
                    },
                }) => (
                    <>
                        {asMoney(
                            value,
                            currencies.find((c) => c.id === currency)
                        )}
                    </>
                ),
            },
            {
                Header: 'Booking Price',
                accessor: 'price',
                id: 'bookingPrice',
                editable: true,
                Cell: (props) => <BookingPriceCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'Creator Payment Amount',
                accessor: 'influencer_payment_amount',
                Cell: ({
                    value,
                    row: {
                        original: { currency },
                    },
                }) => (
                    <>
                        {asMoney(
                            value,
                            currencies.find((c) => c.id === currency)
                        )}
                    </>
                ),
            },
            {
                Header: 'Manager Comm.',
                accessor: 'manager_commission',
                Cell: ({
                    value,
                    row: {
                        original: { id, date_approved },
                    },
                }) => {
                    const [commissionInputValue, setCommissionInputValue] = useState<number | string>(value * 100);
                    const disabled = Boolean(date_approved);
                    const submit = (inputValue: string | number) => {
                        if (typeof updateBooking === 'function' && value !== inputValue) {
                            updateBooking(id, { manager_commission: Number(inputValue) / 100 }).catch(() =>
                                setCommissionInputValue(value * 100)
                            );
                        }
                    };

                    return (
                        <NumberInput
                            suffix="%"
                            decimalPlaces={2}
                            disabled={disabled}
                            value={commissionInputValue}
                            onChange={(e, value) => {
                                setCommissionInputValue(value);
                            }}
                            onBlur={(e, inputValue) => submit(inputValue)}
                            onKeyDown={(e, inputValue) => {
                                if (e.key === 'Enter') {
                                    submit(inputValue);
                                }
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Manager Amount',
                accessor: 'manager_commission_amount',
                Cell: ({
                    value,
                    row: {
                        original: { currency },
                    },
                }) => (
                    <>
                        {asMoney(
                            value,
                            currencies.find((c) => c.id === currency)
                        )}
                    </>
                ),
            },
            {
                Header: 'Agency Comm.',
                accessor: 'agency_commission',
                Cell: ({
                    value,
                    row: {
                        original: { id, date_approved },
                    },
                }) => {
                    const [commissionInputValue, setCommissionInputValue] = useState<number | string>(value * 100);
                    const disabled = Boolean(date_approved);
                    const submit = (inputValue: string | number) => {
                        if (typeof updateBooking === 'function' && value !== inputValue) {
                            updateBooking(id, { agency_commission: Number(inputValue) / 100 }).catch(() =>
                                setCommissionInputValue(value * 100)
                            );
                        }
                    };

                    return (
                        <NumberInput
                            suffix="%"
                            decimalPlaces={2}
                            disabled={disabled}
                            value={commissionInputValue}
                            onChange={(e, value) => {
                                setCommissionInputValue(value);
                            }}
                            onBlur={(e, inputValue) => submit(inputValue)}
                            onKeyDown={(e, inputValue) => {
                                if (e.key === 'Enter') {
                                    submit(inputValue);
                                }
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Status',
                accessor: 'status',
                editable: true,
                Cell: ({
                    value,
                    row: {
                        original: { id },
                    },
                }) => (
                    <StatusPicker
                        status={value}
                        onChange={(status) => {
                            if (typeof onStatusChange === 'function') {
                                onStatusChange(id, status);
                            }
                        }}
                    />
                ),
            },
            {
                Header: 'Post Link',
                accessor: 'post_url',
                editable: true,
                Cell: (props) =>
                    props.row.original.post_type === 'instagram_story' ? (
                        <IGStoryUploadCell updateBooking={updateBooking} {...props} />
                    ) : (
                        <PostLinkCell {...props} updateBooking={updateBooking} />
                    ),
            },
            {
                Header: 'Notes',
                accessor: 'notes',
                editable: true,
                Cell: ({ value, row: { original } }) => {
                    const [notes, setNotes] = useState(value);

                    const submit = () => {
                        if (value !== notes) {
                            updateBooking(original.id, { notes }).catch(() => setNotes(value));
                        }
                    };

                    return (
                        <textarea
                            className={styles.notesInput}
                            placeholder="-"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            onBlur={submit}
                            onKeyDown={(e) => {
                                if (e.shiftKey) {
                                    return;
                                }

                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                }
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Boost Code',
                accessor: 'tiktok_ad_code',
                editable: true,
                Cell: (props) => <BoostCodeCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'PO/Financial Reference',
                accessor: 'finance_reference',
                editable: true,
                Cell: (props) => <FinancialReferenceCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'Reasons To Block Approval',
                accessor: 'reasons_to_block_approval_to_bill',
                Cell: ReasonsToBlockApprovalCell,
            },
            {
                Header: 'Approved To Bill?',
                accessor: 'date_approved',
                id: 'approvedToBill',
                editable: true,
                Cell: (props) => <ApproveToBillCell {...props} approveToBill={approveToBill} />,
            },
            {
                Header: 'Date Approved',
                accessor: 'date_approved',
                Cell: ({ value }) => <>{typeof value === 'string' ? formatDate(value) : ''}</>,
            },
            {
                Header: 'Invoice Number',
                accessor: 'client_invoice_id',
                editable: true,
                dropdown: true,
                Cell: (props) =>
                    !!props.row.original.client_invoice_id &&
                    !props.row.original.xero_invoice &&
                    !hasErrorLoadingInvoices ? (
                        <Skeleton containerClassName={styles.invoiceSkeleton} />
                    ) : (
                        <InvoiceCell {...props} updateBooking={updateBooking} />
                    ),
            },
            {
                Header: 'Invoice Paid?',
                accessor: 'is_invoice_paid',
                Cell: ({ value }) => <>{formatYesNo(value)}</>,
            },
            {
                Header: 'Creator Paid?',
                id: 'creator_paid',
                accessor: 'date_influencer_paid',
                Cell: ({ value }) => <>{formatYesNo(Boolean(value))}</>,
            },
            {
                Header: 'Creator Payment Date',
                accessor: 'date_influencer_paid',
                Cell: ({ value }) => <>{typeof value === 'string' ? formatDate(value) : ''}</>,
            },
            {
                Header: 'Creator Payment Terms',
                accessor: 'payment_terms',
                Cell: ({ value, row: { original } }) => (
                    <PaymentTermsPicker
                        value={value}
                        onChange={async (paymentTerms) => updateBooking(original.id, { payment_terms: paymentTerms })}
                    />
                ),
            },
            {
                Header: 'Manager Paid?',
                accessor: 'is_manager_paid',
                Cell: ({ value }) => <>{formatYesNo(value)}</>,
            },
        ];

        return columns;
    }, [currencies, hasErrorLoadingInvoices, onStatusChange, updateBooking, approveToBill]);

    return (
        <Table
            className={cn(styles.table, className)}
            columns={columns}
            data={data}
            renderRow={(row, onClick) => {
                const { influencer_cost, price } = row.original;
                const isBookingPriceLowerThanAskingPrice = influencer_cost && price < influencer_cost;
                return (
                    <tr
                        {...row.getRowProps()}
                        onClick={(e) => {
                            if (typeof onClick === 'function') {
                                onClick(e, row);
                            }
                        }}
                    >
                        {row.cells.map((cell) => {
                            const isBookingPriceCell = cell.column.id === 'bookingPrice';
                            return (
                                <td
                                    {...cell.getCellProps()}
                                    className={cn({
                                        [styles.editable]: (cell.column as AdminTableColumn).editable,
                                        [styles.dropdown]: (cell.column as AdminTableColumn).dropdown,
                                        [styles.priceWarningCell]:
                                            isBookingPriceCell && isBookingPriceLowerThanAskingPrice,
                                    })}
                                >
                                    {cell.render('Cell')}
                                </td>
                            );
                        })}
                    </tr>
                );
            }}
            renderBody={({ rows, prepareRow, columns }, renderRow) => (
                <tbody>
                    {rows.map((row) => {
                        prepareRow(row);
                        return renderRow(row);
                    })}
                    {loading &&
                        range(0, 10).map((i) => (
                            <tr key={i}>
                                {columns.map((column) => (
                                    <td key={column.id}>
                                        <Skeleton style={{ zIndex: 'unset' }} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            )}
            renderFooter={() => (
                <tfoot>
                    <tr>
                        <td />
                        <td />
                        <td colSpan={9} />
                        <td>{totals ? renderTotalsAsMoney(totals.price, currencies) : '-'}</td>
                        <td colSpan={2} />
                        <td>{totals ? renderTotalsAsMoney(totals.manager_commission, currencies) : '-'}</td>
                        <td colSpan={15} />
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default AdminBookingsTable;
